import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { format } from "date-fns"

import type { MergedRow } from "./schemas"

function useDownloadCsvErrors(rows: MergedRow[]) {
  const { t } = useTranslation()
  const headers = ["Row", "Phone", "Email", "No Call", "Error"]

  const translateKey = (key: string) =>
    t(`noCallList.importCSV.warnings.${key}`, {
      defaultValue: t("noCallList.importCSV.warnings.INTERNAL_ERROR"),
    })

  function getRowErrors(row: MergedRow): string {
    if (row.parseStatus === "invalid") {
      return row.errors.map(translateKey).join("; ")
    }

    if (row.parseStatus === "valid" && row.writeStatus === "error") {
      return (row.writeErrors ?? []).map(translateKey).join("; ")
    }

    if (row.parseStatus === "valid" && row.status === "not_found") {
      return translateKey("STUDENT_NOT_FOUND")
    }

    return ""
  }

  function formatCsvErrorRows(csvRows: MergedRow[]) {
    const errorRows = csvRows.filter(
      (r) =>
        r.parseStatus === "invalid" ||
        (r.parseStatus === "valid" &&
          (r.status === "not_found" || r.writeStatus === "error")),
    )

    if (errorRows.length === 0) {
      return []
    }

    const formattedRows = errorRows.map((row) => {
      const errors = getRowErrors(row)

      return [
        row.rowIndex,
        escapeCsvCell(row.phone ?? ""),
        escapeCsvCell(row.email ?? ""),
        row.parseStatus === "valid" ? row.noCall : (row.noCall ?? ""),
        escapeCsvCell(errors),
      ].join(",")
    })

    return formattedRows
  }

  const downloadErrorCsv = useCallback(() => {
    const formattedRows = formatCsvErrorRows(rows)
    const filename = buildErrorFilename()

    const csv = [headers.join(","), ...formattedRows].join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)

    const link = document.createElement("a")
    link.href = url
    link.download = filename
    link.click()

    URL.revokeObjectURL(url)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  return { downloadErrorCsv }
}

function escapeCsvCell(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`
  }

  return value
}

function buildErrorFilename() {
  return `${format(new Date(), "yyyyMMddHHmmss")}_errors.csv`
}

export { useDownloadCsvErrors }
